import React, { useEffect, useState, useContext } from 'react';
import { 
  Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, 
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, IconButton,
  FormControl, FormHelperText, InputLabel, Select, MenuItem, OutlinedInput, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import useConfirm from '../calendar/dialogs/UseConfirm';
import { useSnackbar } from 'notistack';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function UserConfigPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { user, permissions } = useContext(UserContext);
  const { confirm, ConfirmDialog } = useConfirm();
  const currentEmail = user?.email?.toLowerCase();
  const canRead = permissions.includes('users.read');
  const canWrite = permissions.includes('users.write');

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stations, setStations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [idRole, setIdRole] = useState('');
  const [selectedStations, setSelectedStations] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!email.trim()) errors.email = 'El email es obligatorio.';
    else if (!EMAIL_REGEX.test(email.trim())) errors.email = 'Ingresa un email válido (ej: nombre@empresa.com).';
    if (!name.trim()) errors.name = 'El nombre es obligatorio.';
    if (!idRole) errors.role = 'Selecciona un rol.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const clearFieldError = (field) =>
    setFormErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/users');
      setUsers(res.data);
    } catch {
      enqueueSnackbar('Error al listar usuarios', { variant: 'error' });
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get('/roles');
      setRoles(res.data);
    } catch {
      enqueueSnackbar('Error al listar roles', { variant: 'error' });
    }
  };

  const fetchStations = async () => {
    try {
      const res = await axios.get('station');
      setStations(res.data);
    } catch {
      enqueueSnackbar('Error al listar estaciones', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (canRead) {
      fetchUsers();
    }
    if (canWrite) {
      fetchRoles();
      fetchStations();
    }
  }, [canRead, canWrite]);

  const handleRoleChange = (e) => {
    const selectedId = e.target.value;
    setIdRole(selectedId);
    clearFieldError('role');

    // Buscar el rol por ID
    const selectedRole = roles.find(r => r.id === selectedId);
  
    // Si el rol es "Admin", asignar todas las estaciones
    if (selectedRole && ['admin', 'super_admin'].includes(selectedRole.name.toLowerCase())) {
      const allStationIds = stations.map(s => s.id);
      setSelectedStations(allStationIds);
    } else {
      setSelectedStations([]);
    }
  };

  
  const handleAddOpen = () => {
    setEmail('');
    setName('');
    setIdRole('');
    setSelectedStations([]);
    setFormErrors({});
    setOpen(true);
  };

  const handleAdd = async () => {
    if (!validateForm()) return;

    const params = new URLSearchParams({
      email,
      name,
      id_role: idRole,
      enable: true,
      stations: selectedStations.join(',')
    });

    try {
      await axios.post(`/users?${params.toString()}`);
      setOpen(false);
      fetchUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al crear usuario', { variant: 'error' });
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setEmail(u.email);
    setName(u.name);
    setIdRole(u.id_role);
    setSelectedStations(u.stations || []);
    setFormErrors({});
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!validateForm()) return;

    const params = new URLSearchParams({
      email,
      name,
      id_role: idRole,
      enable: editingUser.enable,
      stations: selectedStations.join(',')
    });

    try {
      await axios.put(`/users/${editingUser.id}?${params.toString()}`);
      setEditOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al editar usuario', { variant: 'error' });
    }
  };

  const handleStationsChange = (e) => {
    const value = e.target.value;
    setSelectedStations(typeof value === 'string'
      ? value.split(',').map(Number)
      : value.map(Number)
    );
  };

  const handleDisable = async (u) => {
    const ok = await confirm({
      title: 'Deshabilitar usuario',
      content: `¿Seguro que querés deshabilitar a "${u.name}" (${u.email})?`,
      confirmText: 'Deshabilitar',
    });
    if (!ok) return;
    try {
      await axios.delete(`/users/${u.id}`);
      fetchUsers();
    } catch {
      enqueueSnackbar('Error al deshabilitar usuario', { variant: 'error' });
    }
  };

  const sortedUsers = [...users].sort((a, b) => a.enable === b.enable ? 0 : a.enable ? -1 : 1);

  if (!canRead) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Gestión de Usuarios
      </Typography>

      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddOpen}>
            Agregar usuario
          </Button>
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Nombre</TableCell>
            <TableCell>Rol</TableCell>
            <TableCell>Estado</TableCell>
            <TableCell align="right">Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedUsers.map(u => (
            <TableRow key={u.id} sx={u.enable ? {} : { bgcolor: '#f2f2f2' }}>
              <TableCell>{u.id}</TableCell>
              <TableCell sx={u.enable ? {} : { color: '#aaa', textDecoration: 'line-through' }}>{u.email}</TableCell>
              <TableCell sx={u.enable ? {} : { color: '#aaa', textDecoration: 'line-through' }}>{u.name}</TableCell>
              <TableCell>{u.role_name}</TableCell>
              <TableCell>
                {u.enable
                  ? <span style={{ color: '#36a16a', fontWeight: 600 }}>Habilitado</span>
                  : <span style={{ color: '#aaa', fontWeight: 600 }}>Deshabilitado</span>}
              </TableCell>
              <TableCell align="right">
                {u.email.toLowerCase() === currentEmail && (
                  <IconButton size="small" onClick={() => {
                    setViewingUser(u);
                    setViewOpen(true);
                  }}>
                    <VisibilityIcon />
                  </IconButton>
                )}
                {canWrite && u.email.toLowerCase() !== currentEmail && (
                  <>
                    <IconButton size="small" onClick={() => handleEdit(u)} disabled={!u.enable}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" title="Deshabilitar" onClick={() => handleDisable(u)} disabled={!u.enable}>
                      <BlockIcon />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {ConfirmDialog}

      {/* ——— Modal Agregar ——— */}
      <Dialog open={open && canWrite} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nuevo usuario</DialogTitle>
        <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Email"
            fullWidth
            required
            value={email}
            onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
            error={Boolean(formErrors.email)}
            helperText={formErrors.email}
          />
          <TextField
            label="Nombre"
            fullWidth
            required
            value={name}
            onChange={e => { setName(e.target.value); clearFieldError('name'); }}
            error={Boolean(formErrors.name)}
            helperText={formErrors.name}
          />
          <FormControl fullWidth required error={Boolean(formErrors.role)}>
            <InputLabel>Rol</InputLabel>
            <Select value={idRole} label="Rol" onChange={handleRoleChange}>
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} – {r.description}
                </MenuItem>
              ))}
            </Select>
            {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Estaciones</InputLabel>
            <Select
              multiple
              value={selectedStations}
              onChange={handleStationsChange}
              input={<OutlinedInput label="Estaciones" />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                  {selected.map(id => {
                    const st = stations.find(s => s.id === id);
                    return <Chip key={id} label={st?.name || id} size="small" />;
                  })}
                </Box>
              )}
            >
              {stations.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} — {s.address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAdd}>Crear</Button>
        </DialogActions>
      </Dialog>

      {/* ——— Modal Editar ——— */}
      <Dialog open={editOpen && canWrite} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar usuario</DialogTitle>
        <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
          <TextField
            label="Email"
            fullWidth
            required
            value={email}
            onChange={e => { setEmail(e.target.value); clearFieldError('email'); }}
            error={Boolean(formErrors.email)}
            helperText={formErrors.email}
          />
          <TextField
            label="Nombre"
            fullWidth
            required
            value={name}
            onChange={e => { setName(e.target.value); clearFieldError('name'); }}
            error={Boolean(formErrors.name)}
            helperText={formErrors.name}
          />
          <FormControl fullWidth required error={Boolean(formErrors.role)}>
            <InputLabel>Rol</InputLabel>
            <Select value={idRole} label="Rol" onChange={e => { setIdRole(e.target.value); clearFieldError('role'); }}>
              {roles.map(r => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} – {r.description}
                </MenuItem>
              ))}
            </Select>
            {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Estaciones</InputLabel>
            <Select
              multiple
              value={selectedStations}
              onChange={handleStationsChange}
              input={<OutlinedInput label="Estaciones" />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                  {selected.map(id => {
                    const st = stations.find(s => s.id === id);
                    return <Chip key={id} label={st?.name || id} size="small" />;
                  })}
                </Box>
              )}
            >
              {stations.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} — {s.address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleEditSave}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* ——— Modal Ver Usuario Actual ——— */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Mis datos</DialogTitle>
        <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
          <TextField label="Email" fullWidth value={viewingUser?.email || ''} InputProps={{ readOnly: true }} />
          <TextField label="Nombre" fullWidth value={viewingUser?.name || ''} InputProps={{ readOnly: true }} />
          <TextField label="Rol" fullWidth value={viewingUser?.role_name || ''} InputProps={{ readOnly: true }} />
          <FormControl fullWidth>
            <InputLabel>Estaciones asignadas</InputLabel>
            <Select
              multiple
              value={viewingUser?.stations || []}
              input={<OutlinedInput label="Estaciones asignadas" />}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .5 }}>
                  {selected.map(id => {
                    const st = stations.find(s => s.id === id);
                    return <Chip key={id} label={st?.name || id} size="small" />;
                  })}
                </Box>
              )}
              readOnly
              disabled
            >
              {stations.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name} — {s.address}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
